import { EventEmitter, Injectable } from "@angular/core";
import { NbComponentStatus, NbToastrService, NbGlobalPhysicalPosition, NbDialogService, NbIconConfig, NbGlobalLogicalPosition } from '@nebular/theme';
import { BigNumber } from "bignumber.js";
import { ChangePasswordDialogComponent } from "../pages/modal-overlays/dialog/change-password-dialog/change-password-dialog.component";
import { ConfirmDialogComponent } from "../pages/modal-overlays/dialog/confirm-dialog/confirm-dialog.component";
import { DetailDialogComponent } from "../pages/modal-overlays/dialog/detail-dialog/detail-dialog.component";
import { MessageDialogComponent } from "../pages/modal-overlays/dialog/message-dialog/message-dialog.component";
import * as crypto from 'crypto-js';
import { CONST } from "./const";
import { ImgCropperOptions } from "../components/img-cropper/img-cropper.component";
import { ImgPickerComponent } from "../pages/modal-overlays/dialog/img-picker/img-picker.component";
import { ConfirmRoleDialogComponent } from "../pages/modal-overlays/dialog/confirm-role-dialog/confirm-role-dialog.component";
import { Clubs, Jobs } from "../public/signup/club/club.model";
import { InputClubStaffRoleDialogComponent } from "../pages/modal-overlays/dialog/input-clubstaff-role-dialog/input-clubstaff-role-dialog.component";
import { UserInfo } from "./auth.provider";
import { AthleteResumenDialogComponent } from "../pages/modal-overlays/dialog/athlete-resumen-dialog/athlete-resumen-dialog.component";

export interface Column {
    id: string;
    name?: string;
    displayable?: boolean;
    update?: boolean;
}

@Injectable()
export class UtilitiesService {
    public validateProfileSideMenu: EventEmitter<any> = new EventEmitter<any>();
    public validateProfilePicture: EventEmitter<any> = new EventEmitter<any>();
    public validateProfileName: EventEmitter<any> = new EventEmitter<any>();
    public validateRoles: EventEmitter<any> = new EventEmitter<any>();

    private static secretKey = "5P0rtST3";
    public static tiempoDespliegue = 5000;

    public static encrypt(data: any) {
        let encryptData = {
            validator: CONST.CRYPTO.validator,
            data: data
        }
        return crypto.AES.encrypt(JSON.stringify(encryptData), this.secretKey.trim()).toString();
    }

    public static decrypt(textToDecrypt: string) {
        let decryptedData = null
        try {
            decryptedData = JSON.parse(crypto.AES.decrypt(textToDecrypt, this.secretKey.trim()).toString(crypto.enc.Utf8));
        } catch (e) {
            decryptedData = null;
        }
        return decryptedData
    }

    public static showToast(toastrService: NbToastrService,
        title: string,
        body: string,
        type?: NbComponentStatus,
        duration?: number,
        position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition,
        iconConfig?: NbIconConfig) {
        let defaultIconConfig: NbIconConfig = { icon: 'comment-alt', pack: 'fa' };

        const config: any = {
            status: type ? type : "basic",
            duration: duration ? duration : duration == 0 ? 0 : 3000,
            position: position ? position : NbGlobalPhysicalPosition.BOTTOM_RIGHT,
            destroyByClick: true,
            preventDuplicates: true,
            hasIcon: true,
            icon: iconConfig ? iconConfig : defaultIconConfig,
        };

        const titleContent = title ? title : '';

        toastrService.show(
            body,
            `${titleContent}`,
            config);
    }

    public static showToastPrimary(toastrService: NbToastrService,
        title: string,
        body: string, duration?: number, position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition) {
        let time = duration || duration == 0 ? duration : this.tiempoDespliegue;
        this.showToast(toastrService, title, body, 'primary', time, position)
    }

    public static showToastSuccess(toastrService: NbToastrService,
        title: string,
        body: string, duration?: number, position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition) {
        let iconConfig: NbIconConfig = { icon: 'check', pack: 'fa' }
        let time = duration || duration == 0 ? duration : this.tiempoDespliegue;
        this.showToast(toastrService, title, body, 'success', time, position, iconConfig)
    }

    public static showToastError(toastrService: NbToastrService,
        title: string,
        body: string, duration?: number, position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition) {
        let iconConfig: NbIconConfig = { icon: 'exclamation-circle', pack: 'fa' }
        let time = duration || duration == 0 ? duration : 0;
        this.showToast(toastrService, title, body, 'danger', time, position, iconConfig)
    }

    public static showToastInfo(toastrService: NbToastrService,
        title: string,
        body: string, duration?: number, position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition) {
        let iconConfig: NbIconConfig = { icon: 'info-circle', pack: 'fa' }
        let time = duration || duration == 0 ? duration : this.tiempoDespliegue;
        this.showToast(toastrService, title, body, 'info', time, position, iconConfig)
    }

    public static showToastWarning(toastrService: NbToastrService,
        title: string,
        body: string, duration?: number, position?: NbGlobalPhysicalPosition | NbGlobalLogicalPosition) {
        let iconConfig: NbIconConfig = { icon: 'exclamation-triangle', pack: 'fa' }
        let time = duration || duration == 0 ? duration : this.tiempoDespliegue;
        this.showToast(toastrService, title, body, 'warning', time, position, iconConfig)
    }


    public static showConfirmDialog(dialogService: NbDialogService, title: string, message: string, question?: string, yesOption?: string, noOption?: string) {
        return new Promise(resolve => {
            dialogService.open(ConfirmDialogComponent, {
                context: {
                    title: title,
                    message: message,
                    question: question ? question : null,
                    yes: yesOption ? yesOption : "Aceptar",
                    no: noOption ? noOption : 'Cancelar'
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showConfirmRoleDialog(dialogService: NbDialogService, role: string) {
        return new Promise(resolve => {
            dialogService.open(ConfirmRoleDialogComponent, {
                context: {
                    role: role
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showChangePasswordDialog(dialogService: NbDialogService) {
        return new Promise(resolve => {
            dialogService.open(ChangePasswordDialogComponent).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showImgPicker(dialogService: NbDialogService, options: ImgCropperOptions) {
        return new Promise(resolve => {
            dialogService.open(ImgPickerComponent, {
                context: {
                    options: options
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showMessageDialog(dialogService: NbDialogService, title: string, message: string, okOption?: string) {
        return new Promise(resolve => {
            dialogService.open(MessageDialogComponent, {
                context: {
                    title: title,
                    message: message,
                    ok: okOption ? okOption : "Aceptar",
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showAthleteResumenDialog(dialogService: NbDialogService, data: any) {
        return new Promise(resolve => {
            dialogService.open(AthleteResumenDialogComponent, {
                context: {
                    data: data
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    public static showInputClubStaffRoleDialog(dialogService: NbDialogService, element: any, country, state, jobsTitle?: Jobs[]) {
        return new Promise(resolve => {
            dialogService.open(InputClubStaffRoleDialogComponent, {
                context: {
                    element: element,
                    country: country,
                    state: state,
                    jobsTitle: jobsTitle
                },
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }

    /**
    * 
    * @param title Titulo
    * @param message Mensaje
    * @param data Data que se muestra en el dialogo
    * 
    */
    public static showDetailDialog(dialogService: NbDialogService, title: string, data: { titulo: string, valor: any, isOneRow?: boolean }[], dataEvento?: any): Promise<any> {
        return new Promise(resolve => {

            let columns = [];
            let dataSource = [];

            for (let index = 0; index <= data.length; index++) {
                const element = data[index];
                if (element) {
                    columns.push(element);
                    if (element.isOneRow || columns.length == 2 || (index == data.length && columns.length == 1)) {
                        dataSource.push({ columns: columns });
                        columns = [];
                    }
                }


            }

            let dataDialog = {};

            if (!dataEvento) {
                dataDialog = {
                    title: title,
                    dataSource: dataSource,
                }
            } else {
                dataDialog = {
                    title: title,
                    dataSource: dataSource,
                    dataEvento: dataEvento.detalle,
                    claves: dataEvento.claves,
                    cambioRecurso: dataEvento.cambioRecurso,
                }
            }
            dialogService.open(DetailDialogComponent, {
                context: dataDialog,
            }).onClose.subscribe(res => {
                resolve(res);
            });
        });
    }


    public static timeToSeconds(time) {

        var timeInSec = 0.0;
        var timeMin = +time.minutes;
        var timeSec = +time.seconds;
        var timeMsec = time.milliseconds;

        timeInSec = +(((timeMin * 60) + timeSec) + "." + timeMsec);

        // ((minutes * 60) + seconds) +"."+ milis
        var saux = "";
        var aux = timeInSec;
        var minutes = 0;
        var seconds = 0;
        var milliseconds = 0;

        // Se hace la reIngenieria para verificar que el valor en segudos corresponde al time de Entrada
        if (aux >= 60) {
            minutes = Math.floor(aux / 60);
            saux = ("0" + minutes).slice(-2);
            aux = aux - (minutes * 60);
        } else {
            saux = "00";
        }

        if (aux >= 1) {
            seconds = Math.floor(aux);
            saux = saux + ":" + ("0" + seconds).slice(-2);
            aux = aux - seconds;
        } else {
            saux = saux + ":00";
        }

        if (aux == 0) {
            saux = saux + ":00";
        } else {
            var aux2 = aux * 1000;
            if ((aux2 - Math.floor(aux2)) < 0.97) {
                milliseconds = Math.floor(aux2);
            } else {
                milliseconds = Math.round(aux2);
            }
            saux = saux + ":" + ("0" + milliseconds).slice(-3);
        }

        return { seconds: timeInSec, timeIn: saux };

    }

    public static secondsToTime(seconds) : any {

      let textValue = null;

      if (seconds == 0) return "00:00:000";

      var timeMin = parseInt(new BigNumber(seconds).dividedBy(60).dp(3, BigNumber.ROUND_HALF_EVEN).toFixed(3));

      textValue = timeMin == 0 ? "00:" : (timeMin > 9 ? timeMin+ ":" : "0"+timeMin+":");

      if (timeMin > 0) seconds = seconds - (timeMin * 60);

      textValue += parseInt(new BigNumber(seconds).toFixed(0)) == 0 ? "00:" : (parseInt(new BigNumber(seconds).toFixed(0)) > 9 ? parseInt(new BigNumber(seconds).toFixed(0)) + ":" : "0"+ parseInt(new BigNumber(seconds).toFixed(0)) +":");

      seconds = new BigNumber(seconds).minus(new BigNumber(new BigNumber(seconds).toFixed(0))).dp(3, BigNumber.ROUND_HALF_EVEN);
      seconds = seconds * 1000;

      textValue += parseInt(new BigNumber(seconds).toFixed(0)) == 0 ? "000" : parseInt(new BigNumber(seconds).toFixed(0)) > 99 ? parseInt(new BigNumber(seconds).toFixed(0)) : parseInt(new BigNumber(seconds).toFixed(0)) > 9 ? "0"+ parseInt(new BigNumber(seconds).toFixed(0)) : "00" + parseInt(new BigNumber(seconds).toFixed(0));

      return { timeFormat: textValue, secondsIn: seconds };
  }

}


/**
     * convertir hora a Date Formato 24:00
     * @param hora 
     */
export function hourToDate(tiempo: string): Date {
    if (tiempo.split(":").length == 2) {
        let hora = tiempo.split(":")[0];
        let minutos = tiempo.split(":")[1];
        return new Date(2020, 1, 1, +hora, +minutos);
    } else {
        return new Date();
    }

}

/**
* convertir Date a hora Formato 24:00
* @param hora 
*/
export function dateToHours(tiempo: Date): String {
    let h = tiempo.getHours();
    let m = tiempo.getMinutes();

    let hs = h + "";
    let ms = m + "";
    if (h < 10) {
        hs = "0" + h;
    }
    if (m < 10) {
        ms = "0" + m;
    }

    return hs + ":" + ms;
}
export function stringToDate(
    value: string,
    format: 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy/MM/dd' | 'yyyy/dd/MM' | 'dd-MM-yyyy' | 'MM-dd-yyyy' | 'yyyy-MM-dd' | 'yyyy-dd-MM'
) {
    switch (format) {
        case 'dd/MM/yyyy':
            let [day, month, year] = value.split("/");
            return new Date(+year, +month - 1, +day);
        case 'MM/dd/yyyy':
            let [month2, day2, year2] = value.split("/");
            return new Date(+year2, +month2 - 1, +day2);
        case 'yyyy/MM/dd':
            let [year3, month3, day3] = value.split("/");
            return new Date(+year3, +month3 - 1, +day3);
        case 'yyyy/dd/MM':
            const [year4, day4, month4] = value.split("/");
            return new Date(+year4, +month4 - 1, +day4);

        case 'dd-MM-yyyy':
            let [day5, month5, year5] = value.split("-");
            return new Date(+year5, +month5 - 1, +day5);
        case 'MM-dd-yyyy':
            let [month6, day6, year6] = value.split("-");
            return new Date(+year6, +month6 - 1, +day6);
        case 'yyyy-MM-dd':
            let [year7, month7, day7] = value.split("-");
            return new Date(+year7, +month7 - 1, +day7);
        case 'yyyy-dd-MM':
            const [year8, day8, month8] = value.split("-");
            return new Date(+year8, +month8 - 1, +day8);
    }
}

export function dateToString(
    value: Date,
    format: 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy/MM/dd' | 'yyyy/dd/MM' | 'dd-MM-yyyy' | 'MM-dd-yyyy' | 'yyyy-MM-dd' | 'yyyy-dd-MM'
) {
    let dd: any = value.getDate();
    let mm: any = value.getMonth() + 1;
    let yyyy: any = value.getFullYear();

    if (dd < 10) {
        dd = '0' + dd
    }
    if (mm < 10) {
        mm = '0' + mm
    }

    switch (format) {
        case 'dd/MM/yyyy':
            return dd + "/" + mm + "/" + yyyy;
        case 'MM/dd/yyyy':
            return mm + "/" + dd + "/" + yyyy;
        case 'yyyy/MM/dd':
            return yyyy + "/" + mm + "/" + dd;
        case 'yyyy/dd/MM':
            return yyyy + "/" + dd + "/" + mm;

        case 'dd-MM-yyyy':
            return dd + "-" + mm + "-" + yyyy;
        case 'MM-dd-yyyy':
            return mm + "-" + dd + "-" + yyyy;
        case 'yyyy-MM-dd':
            return yyyy + "-" + mm + "-" + dd;
        case 'yyyy-dd-MM':
            return yyyy + "-" + dd + "-" + mm;
    }
}


export function pipeDecimalBigNumber(monto) {
    let fmt = {
        prefix: '',
        decimalSeparator: ',',
        groupSeparator: '.',
        groupSize: 3,
        secondaryGroupSize: 3
    };

    let amount = new BigNumber(monto).toFormat(2, fmt);

    return amount;
}
